import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Globe, Bell, Moon, Sun, Palette, Youtube, ImageIcon, Upload, Trash2, CalendarIcon } from "lucide-react";
import AdminPageBackground from "@/components/admin/AdminPageBackground";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { useState, useEffect, useRef } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useLiveStream } from "@/contexts/LiveStreamContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const Settings = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { toast } = useToast();
  const { youtubeUrl, setYoutubeUrl } = useLiveStream();
  const queryClient = useQueryClient();
  
  const popupFileRef = useRef<HTMLInputElement>(null);
  const [uploadingPopupImage, setUploadingPopupImage] = useState(false);

  // Founding day popup setting
  const { data: popupSettings } = useQuery({
    queryKey: ["founding_day_popup"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "founding_day_popup")
        .single();
      return (data?.setting_value as any) ?? { enabled: true, image_url: "", expiry_date: "" };
    },
  });

  const popupEnabled = popupSettings?.enabled ?? true;
  const popupImageUrl = popupSettings?.image_url || "";
  const popupExpiryDate = popupSettings?.expiry_date ? new Date(popupSettings.expiry_date) : undefined;

  const updatePopupSetting = useMutation({
    mutationFn: async (newValue: { enabled: boolean; image_url: string; expiry_date: string }) => {
      const { error } = await supabase
        .from("site_settings")
        .update({ setting_value: newValue as any, updated_at: new Date().toISOString() })
        .eq("setting_key", "founding_day_popup");
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["founding_day_popup"] });
    },
  });

  const handleTogglePopup = (checked: boolean) => {
    updatePopupSetting.mutate({ enabled: checked, image_url: popupImageUrl, expiry_date: popupSettings?.expiry_date || "" });
    toast({
      title: isRTL ? "تم الحفظ" : "Saved",
      description: checked
        ? (isRTL ? "تم تفعيل النافذة المنبثقة" : "Popup enabled")
        : (isRTL ? "تم تعطيل النافذة المنبثقة" : "Popup disabled"),
    });
  };

  const handlePopupImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: isRTL ? "خطأ" : "Error", description: isRTL ? "حجم الصورة يجب أن يكون أقل من 5 ميجابايت" : "Image must be less than 5MB", variant: "destructive" });
      return;
    }
    setUploadingPopupImage(true);
    try {
      const fileName = `popup-${Date.now()}.${file.name.split('.').pop()}`;
      const { error: uploadError } = await supabase.storage.from("popup-images").upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from("popup-images").getPublicUrl(fileName);
      updatePopupSetting.mutate({ enabled: popupEnabled, image_url: urlData.publicUrl, expiry_date: popupSettings?.expiry_date || "" });
      toast({ title: isRTL ? "تم الرفع" : "Uploaded", description: isRTL ? "تم تحديث صورة النافذة المنبثقة" : "Popup image updated" });
    } catch (err) {
      toast({ title: isRTL ? "خطأ" : "Error", description: isRTL ? "فشل رفع الصورة" : "Failed to upload image", variant: "destructive" });
    } finally {
      setUploadingPopupImage(false);
      if (popupFileRef.current) popupFileRef.current.value = "";
    }
  };

  const handleRemovePopupImage = () => {
    updatePopupSetting.mutate({ enabled: popupEnabled, image_url: "", expiry_date: popupSettings?.expiry_date || "" });
    toast({ title: isRTL ? "تم الحذف" : "Removed", description: isRTL ? "تم حذف صورة النافذة المنبثقة، سيتم استخدام الصورة الافتراضية" : "Popup image removed, default will be used" });
  };

  const defaultColors = {
    primary: "#2563eb",
    secondary: "#64748b",
    background: "#ffffff",
    text: "#1e293b",
    buttonText: "#ffffff"
  };
  
  const [notifications, setNotifications] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [emailUpdates, setEmailUpdates] = useState(true);
  const [colors, setColors] = useState(defaultColors);

  const handleColorChange = (colorKey: string, value: string) => {
    setColors(prev => ({ ...prev, [colorKey]: value }));
  };

  const handleResetToDefault = () => {
    setColors(defaultColors);
    toast({
      title: isRTL ? "تم إعادة تعيين الألوان" : "Colors Reset",
      description: isRTL ? "تم إعادة تعيين الألوان إلى الإعدادات الافتراضية" : "Colors have been reset to default values",
    });
  };

  const handleSaveColors = () => {
    toast({
      title: isRTL ? "تم حفظ الألوان" : "Colors Saved",
      description: isRTL ? "تم تحديث ألوان الموقع بنجاح" : "Website colors have been updated successfully",
    });
  };

  const handleSaveYoutubeUrl = () => {
    if (!youtubeUrl) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "الرجاء إدخال رابط البث المباشر" : "Please enter the live stream URL",
        variant: "destructive",
      });
      return;
    }

    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
    if (!youtubeRegex.test(youtubeUrl)) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "الرجاء إدخال رابط يوتيوب صحيح" : "Please enter a valid YouTube URL",
        variant: "destructive",
      });
      return;
    }

    setYoutubeUrl(youtubeUrl);
    toast({
      title: isRTL ? "تم الحفظ" : "Saved",
      description: isRTL ? "تم حفظ رابط البث المباشر بنجاح" : "Live stream URL has been saved successfully",
    });
  };

  return (
    <AdminPageBackground className={isRTL ? "rtl" : "ltr"}>
      <nav className="border-b border-white/10 bg-white/5 backdrop-blur-sm">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/admin-dashboard"
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {isRTL ? "العودة للوحة التحكم" : "Back to Dashboard"}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")}
              className="rounded-full text-white/70 hover:text-white hover:bg-white/10"
            >
              <Globe className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold mb-8 text-white">
          {isRTL ? "الإعدادات" : "Settings"}
        </h1>

        <div className="bg-white/10 backdrop-blur-md rounded-lg border border-white/15 p-6 space-y-6">
          {/* البث المباشر / Live Stream */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Youtube className="w-5 h-5 text-red-400" />
              <h2 className="text-xl font-semibold text-white">
                {isRTL ? "رابط البث المباشر" : "Live Stream URL"}
              </h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="mb-2 block text-white/70">
                  {isRTL ? "رابط البث المباشر على يوتيوب" : "YouTube Live Stream URL"}
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="url"
                    placeholder={isRTL ? "أدخل رابط البث المباشر..." : "Enter live stream URL..."}
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    className="flex-1 bg-white/10 border-white/15 text-white placeholder:text-white/40"
                    dir="ltr"
                  />
                  <Button 
                    onClick={handleSaveYoutubeUrl}
                    className="flex-shrink-0"
                  >
                    {isRTL ? "حفظ الرابط" : "Save URL"}
                  </Button>
                </div>
                <p className="text-sm text-white/40 mt-2">
                  {isRTL 
                    ? "أدخل رابط البث المباشر من يوتيوب. مثال: https://youtube.com/watch?v=..." 
                    : "Enter your YouTube live stream URL. Example: https://youtube.com/watch?v=..."
                  }
                </p>
              </div>
            </div>
          </div>

          <Separator className="bg-white/10" />

          {/* النافذة المنبثقة / Popup */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon className="w-5 h-5 text-purple-400" />
              <h2 className="text-xl font-semibold text-white">
                {isRTL ? "النافذة المنبثقة (يوم التأسيس)" : "Popup (Founding Day)"}
              </h2>
            </div>
            <div className="flex items-center justify-between mb-4">
              <div className="space-y-1">
                <Label className="text-white">
                  {isRTL ? "تفعيل النافذة المنبثقة" : "Enable Popup"}
                </Label>
                <div className="text-sm text-white/50">
                  {isRTL ? "عرض نافذة يوم التأسيس للزوار عند فتح الموقع" : "Show Founding Day popup to visitors on site load"}
                </div>
              </div>
              <Switch
                checked={popupEnabled}
                onCheckedChange={handleTogglePopup}
                disabled={updatePopupSetting.isPending}
              />
            </div>

            {/* Image upload */}
            <div className="space-y-3">
              <Label className="text-white">
                {isRTL ? "صورة النافذة المنبثقة" : "Popup Image"}
              </Label>
              <input
                ref={popupFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePopupImageUpload}
              />
              {popupImageUrl ? (
                <div className="relative rounded-lg overflow-hidden border border-white/10 max-w-xs">
                  <img src={popupImageUrl} alt="Popup" className="w-full h-auto" />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => popupFileRef.current?.click()} disabled={uploadingPopupImage}>
                      <Upload className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="destructive" className="h-8 w-8" onClick={handleRemovePopupImage}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors max-w-xs"
                  onClick={() => popupFileRef.current?.click()}
                >
                  <Upload className="h-8 w-8 text-white/30 mx-auto mb-2" />
                  <p className="text-sm text-white/50">
                    {uploadingPopupImage
                      ? (isRTL ? "جاري الرفع..." : "Uploading...")
                      : (isRTL ? "اضغط لرفع صورة (الصورة الافتراضية مستخدمة حالياً)" : "Click to upload (default image in use)")
                    }
                  </p>
                </div>
              )}
            </div>

            {/* Expiry Date */}
            <div className="space-y-3 mt-4">
              <Label className="text-white">
                {isRTL ? "تاريخ انتهاء النافذة المنبثقة" : "Popup Expiry Date"}
              </Label>
              <div className="flex items-center gap-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[240px] justify-start text-left font-normal border-white/20 bg-white/10 text-white hover:bg-white/20",
                        !popupExpiryDate && "text-white/40"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {popupExpiryDate
                        ? format(popupExpiryDate, "PPP", { locale: isRTL ? ar : undefined })
                        : (isRTL ? "اختر تاريخ الانتهاء" : "Pick expiry date")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white" align="start">
                    <Calendar
                      mode="single"
                      selected={popupExpiryDate}
                      onSelect={(date) => {
                        const dateStr = date ? date.toISOString() : "";
                        updatePopupSetting.mutate({ enabled: popupEnabled, image_url: popupImageUrl, expiry_date: dateStr });
                        toast({
                          title: isRTL ? "تم الحفظ" : "Saved",
                          description: date
                            ? (isRTL ? `تاريخ الانتهاء: ${format(date, "PPP", { locale: ar })}` : `Expiry: ${format(date, "PPP")}`)
                            : (isRTL ? "تم إزالة تاريخ الانتهاء" : "Expiry date removed"),
                        });
                      }}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
                {popupExpiryDate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300 hover:bg-white/10"
                    onClick={() => {
                      updatePopupSetting.mutate({ enabled: popupEnabled, image_url: popupImageUrl, expiry_date: "" });
                      toast({
                        title: isRTL ? "تم الحذف" : "Removed",
                        description: isRTL ? "تم إزالة تاريخ الانتهاء" : "Expiry date removed",
                      });
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    {isRTL ? "إزالة" : "Remove"}
                  </Button>
                )}
              </div>
              <p className="text-sm text-white/40">
                {isRTL
                  ? "النافذة المنبثقة ستختفي تلقائياً بعد هذا التاريخ"
                  : "Popup will automatically disappear after this date"}
              </p>
            </div>
          </div>

          <Separator className="bg-white/10" />

          {/* اللغة / Language */}
          <div>
            <h2 className="text-xl font-semibold mb-4 text-white">
              {isRTL ? "اللغة" : "Language"}
            </h2>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="font-medium text-white">
                  {isRTL ? "لغة النظام" : "System Language"}
                </div>
                <div className="text-sm text-white/50">
                  {isRTL ? "اختر لغة واجهة المستخدم" : "Choose interface language"}
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")}
                className="border-white/20 bg-white/10 text-white hover:bg-white/20"
              >
                {i18n.language === "en" ? "English" : "العربية"}
              </Button>
            </div>
          </div>

          <Separator className="bg-white/10" />

          {/* الإشعارات / Notifications */}
          <div>
            <h2 className="text-xl font-semibold mb-4 text-white">
              {isRTL ? "الإشعارات" : "Notifications"}
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-white">
                    {isRTL ? "إشعارات النظام" : "System Notifications"}
                  </Label>
                  <div className="text-sm text-white/50">
                    {isRTL ? "تلقي إشعارات النظام" : "Receive system notifications"}
                  </div>
                </div>
                <Switch
                  checked={notifications}
                  onCheckedChange={setNotifications}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-white">
                    {isRTL ? "تحديثات البريد الإلكتروني" : "Email Updates"}
                  </Label>
                  <div className="text-sm text-white/50">
                    {isRTL ? "تلقي تحديثات عبر البريد الإلكتروني" : "Receive email updates"}
                  </div>
                </div>
                <Switch
                  checked={emailUpdates}
                  onCheckedChange={setEmailUpdates}
                />
              </div>
            </div>
          </div>

          <Separator className="bg-white/10" />

          {/* المظهر / Appearance */}
          <div>
            <h2 className="text-xl font-semibold mb-4 text-white">
              {isRTL ? "المظهر" : "Appearance"}
            </h2>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="text-white">
                  {isRTL ? "الوضع المظلم" : "Dark Mode"}
                </Label>
                <div className="text-sm text-white/50">
                  {isRTL ? "تفعيل الوضع المظلم" : "Enable dark mode"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Sun className="w-4 h-4 text-white/50" />
                <Switch
                  checked={darkMode}
                  onCheckedChange={setDarkMode}
                />
                <Moon className="w-4 h-4 text-white/50" />
              </div>
            </div>
          </div>
        </div>
      </main>
    </AdminPageBackground>
  );
};

export default Settings;
